import type React from 'react';

import {
  AppBar,
  CircularProgress,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { ChevronLeft } from 'lucide-react';
import { Link, type To } from 'react-router-dom';

import { ActionsMenu } from '@modules/_shared/components/actions-menu/ActionsMenu';
import type { Actions } from '@modules/_shared/components/actions-menu/ActionsMenu';
import { ChatToggleButton } from '@modules/_shared/components/chat/chatPanel';
import { CountBadge } from '@modules/_shared/components/CountBadge';
import { useLocale } from '@shared/hooks/locale';
import { TOP_BAR_HEIGHT } from '@shared/theme/layout';

interface DrawerTopBarProps {
  title?: React.ReactNode;
  actions?: Actions[];
  actionsLoading: boolean;
  onClose?: () => void;
  tabs?: DrawerTab[];
  currentTab?: string;
}

export type DrawerTab = {
  label: string;
  icon?: React.ReactElement;
  value: string;
  badge?: number;
  disabled?: boolean;
  tooltip?: string;
  onClick?: () => void;
  to: To;
};

export const generateTab = ([search, label, value, icon, disabled, tooltip]: [
  search: string,
  label: string,
  value: string,
  icon?: React.ReactElement,
  disabled?: boolean,
  tooltip?: string,
]): DrawerTab => ({
  label,
  value,
  icon,
  to: {
    pathname: value,
    search,
  },
  disabled,
  tooltip,
});

export const DrawerTopBar = ({
  title,
  actions,
  actionsLoading,
  onClose,
  tabs,
  currentTab,
}: DrawerTopBarProps) => {
  const { t } = useLocale();

  return (
    <AppBar
      position="sticky"
      sx={theme => ({
        top: 0,
        bottom: 'auto',
        zIndex: theme.zIndex.drawer + 1,
        bgcolor: (theme.vars ?? theme).palette.background.paper,
        color: (theme.vars ?? theme).palette.text.primary,
        borderBottom: `1px solid ${theme.palette.divider}`,
      })}
      elevation={0}
      color="transparent"
    >
      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
          minHeight: TOP_BAR_HEIGHT,
          borderBottom: tabs
            ? theme => `1px solid ${theme.palette.divider}`
            : undefined,
          pl: 1,
          pr: 0.5,
        }}
      >
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            bgcolor: 'grey.100',
            '&:hover': { bgcolor: 'grey.200' },
            transition: 'background-color 0.15s ease',
          }}
        >
          <ChevronLeft size={20} />
        </IconButton>
        <Typography
          variant="h6"
          sx={{ flexGrow: 1, px: 1, fontWeight: 600 }}
          noWrap
        >
          {title}
        </Typography>
        <ChatToggleButton sx={{ mx: 0.5 }} />
        {actions && (
          <Stack
            direction="row"
            sx={{
              justifyContent: 'flex-end',
              alignItems: 'center',
              height: '100%',
              p: 1,
              pr: 2,
            }}
          >
            <ActionsMenu
              label={
                <Stack
                  direction="row"
                  sx={{
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  {t('Actions')}
                  {actionsLoading && (
                    <CircularProgress
                      disableShrink
                      size={14}
                      sx={{ color: 'text.secondary' }}
                    />
                  )}
                </Stack>
              }
              actions={actions}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            />
          </Stack>
        )}
      </Stack>
      {tabs && (
        <Tabs
          value={currentTab ?? false}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            pl: 2,
            '.MuiTabs-scrollButtons.Mui-disabled': {
              opacity: 0.3,
            },
          }}
        >
          {tabs.map(tab => (
            <Tab
              key={tab.value}
              value={tab.value}
              label={
                tab.badge != null && tab.badge > 0 ? (
                  <Stack
                    direction="row"
                    component="span"
                    sx={{ alignItems: 'center' }}
                  >
                    {tab.label}
                    <CountBadge count={tab.badge} />
                  </Stack>
                ) : (
                  tab.label
                )
              }
              icon={tab.icon}
              iconPosition="start"
              disabled={tab.disabled}
              title={tab.tooltip}
              onClick={tab.onClick}
              component={tab.disabled ? 'div' : Link}
              {...(!tab.disabled && { to: tab.to })}
              sx={{
                '&.Mui-disabled': {
                  cursor: tab.tooltip == null ? 'default' : 'not-allowed',
                  pointerEvents: tab.tooltip == null ? 'none' : 'auto',
                },
              }}
            />
          ))}
        </Tabs>
      )}
    </AppBar>
  );
};
